/**
 * Boot context every page can read (round 47). app.ts sets it exactly once, from
 * /api/meta, BEFORE any page loader runs - pages must never fetch /api/meta again
 * (the boot sequence already awaited it; a second fetch is a race with sign-out).
 *
 * The defaults (selfhost, operator) are what a failed /api/meta must fall back to:
 * hiding surfaces on an error would make a transient fetch failure look like a
 * permissions change. The server enforces the real walls either way - these flags
 * only decide what the UI offers.
 */

let saasSession = false;
let operatorSession = true;
let guideOpen = false;
let renderEngine: string | null = null;
let guestSession = false;
let exampleSession = false;
let googleLogin = false;
let emailDelivery = "console";

export function setAppContext(ctx: {
	saas: boolean;
	operator: boolean;
	guideOpen: boolean;
	renderEngine?: string | null;
	guest?: boolean;
	example?: boolean;
	google?: boolean;
	emailDelivery?: string;
}): void {
	saasSession = ctx.saas;
	operatorSession = ctx.operator;
	guideOpen = ctx.guideOpen;
	renderEngine = ctx.renderEngine ?? null;
	guestSession = ctx.guest ?? false;
	exampleSession = ctx.example ?? false;
	googleLogin = ctx.google ?? false;
	emailDelivery = ctx.emailDelivery ?? "console";
}

/**
 * An unclaimed GUEST (§AM): started with "Get started", no email yet. Read by the chrome that offers the
 * claim; the walls on individual controls read `billing.plan.claimed` instead, because a claim in another
 * tab has to reach them without a reload (the plan-wall module's reason for fetching per page).
 */
export function isGuest(): boolean {
	return guestSession;
}

/**
 * Looking at the EXAMPLE workspace (`/example`): made up and read-only. The server refuses every change
 * on its own; this is for chrome that should not OFFER one - the help chat, which would open a ticket
 * about a workspace nobody owns.
 */
export function isExample(): boolean {
	return exampleSession;
}

/** Whether "Continue with Google" can be offered at all (the claim dialog, like the sign-in screen) */
export function googleLoginAvailable(): boolean {
	return googleLogin;
}

/** "resend" once real mail delivery exists, "console" before (the login page's same question) */
export function authEmailDelivery(): string {
	return emailDelivery;
}

/** True when signed into a saas server (selfhost is never "saas" here) */
export function isSaas(): boolean {
	return saasSession;
}

/** Whether this session may use the installation-level settings (selfhost: always) */
export function isOperator(): boolean {
	return operatorSession;
}

/**
 * Whether the first-run guide is still open - the same fact that reveals its nav item.
 *
 * ⚠ **It is here rather than read off the shell's DOM because a second surface now needs it**
 * (2026-08-20): the dashboard's own empty state would otherwise offer "where to start" beside a nav
 * item called Get started, which is two answers to one question - and the guide is the better one,
 * because it knows which steps are done. **When a boot fact grows a second reader, it belongs in this
 * module, not in a `classList` somebody greps for.**
 */
export function isGuideOpen(): boolean {
	return guideOpen;
}

/**
 * Where ffmpeg.wasm is served from, when this server offers the client render lane (§AJ) - null
 * everywhere else, and null IS the answer "the lane does not exist here" (selfhost, failed boot
 * fetch), which the render decision in frontend/renderEngine.ts treats as its first refusal rung.
 */
export function renderEngineBase(): string | null {
	return renderEngine;
}
